import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import { useMemo } from 'react';
import { ReviewLevel } from '@scale/shared/defaults';
import _ from 'lodash';

/* -----------------------------------------------------------------------------

 *  Functions related to configs
 * ----------------------------------------------------------------------------- */
export function getDisableCopyingAndPastingConfigsForContext(
  reviewLevel: ReviewLevel | undefined,
  chatTaskFeatureConfig: IChatTaskFeatureConfig | undefined,
  taskTemplateVariables?: Record<string, string>,
) {
  let disablePromptPasting =
    !_.isNil(reviewLevel) &&
    _.includes(
      (chatTaskFeatureConfig?.disablePasting?.prompt?.enabledReviewLevels ?? []) as ReviewLevel[],
      reviewLevel,
    );

  // If enable_prompt_pasting is any non-empty string, override config and allow prompt pasting
  const ttvShouldEnablePasting = taskTemplateVariables?.enable_prompt_pasting;
  if (_.isString(ttvShouldEnablePasting) && ttvShouldEnablePasting.trim() !== '') {
    disablePromptPasting = false;
  }

  return {
    disablePromptPasting,
    disableResponsePasting:
      !_.isNil(reviewLevel) &&
      _.includes(
        (chatTaskFeatureConfig?.disablePasting?.response?.enabledReviewLevels ??
          []) as ReviewLevel[],
        reviewLevel,
      ),
    disablePastingTextCollectionFieldIds: !_.isNil(reviewLevel)
      ? _.map(
          _.filter(
            chatTaskFeatureConfig?.disablePasting?.textCollectionFields ?? [],
            ({ reviewLevels }) => {
              return ((reviewLevels ?? []) as ReviewLevel[]).includes(reviewLevel);
            },
          ),
          ({ fieldId }) => fieldId,
        )
      : [],
    disablePastingAllTextCollectionFields:
      !_.isNil(reviewLevel) &&
      _.includes(
        (chatTaskFeatureConfig?.disablePasting?.allTextCollectionFields?.enabledReviewLevels ??
          []) as ReviewLevel[],
        reviewLevel,
      ),
    disableAutoTyperTextRemoval: chatTaskFeatureConfig?.disablePasting?.disableAutoTyperTextRemoval,
    disablePromptCopying:
      !_.isNil(reviewLevel) &&
      _.includes(
        (chatTaskFeatureConfig?.disableCopying?.prompt?.enabledReviewLevels ?? []) as ReviewLevel[],
        reviewLevel,
      ),
    disableResponseSelectorCopying:
      !_.isNil(reviewLevel) &&
      _.includes(
        (chatTaskFeatureConfig?.disableCopying?.responseSelector?.enabledReviewLevels ??
          []) as ReviewLevel[],
        reviewLevel,
      ),
  };
}

export function getChatTaskFeatureConfigsForContext(
  chatTaskFeatureConfig: IChatTaskFeatureConfig | undefined,
) {
  return {
    milkdownEditorConfig: chatTaskFeatureConfig?.milkdownEditorConfig,
    editorConfig: chatTaskFeatureConfig?.editorConfig,
    htmlMarkdownConfig: chatTaskFeatureConfig?.htmlMarkdownConfig,
    generalMarkdownConfig: chatTaskFeatureConfig?.generalMarkdownConfig,
    codingConfig: chatTaskFeatureConfig?.codingConfig,
    languageConfig: chatTaskFeatureConfig?.languageConfig,
    autoCritiqueConfig: chatTaskFeatureConfig?.autoCritique,
    modelResponseConfig: chatTaskFeatureConfig?.modelResponseConfig,
    enableWordCounter: chatTaskFeatureConfig?.enableWordCounter,
    enableFontSizeSelector: chatTaskFeatureConfig?.enableFontSizeSelector,
    enableLanguageTool: chatTaskFeatureConfig?.enableLanguageTool,
    enableCodingUnitTestsGeneration: chatTaskFeatureConfig?.enableCodingUnitTestsGeneration,
    enableFactChecker: chatTaskFeatureConfig?.enableFactChecker,
    alwaysAddSystemPromptAsFirstMessage: chatTaskFeatureConfig?.alwaysAddSystemPromptAsFirstMessage,
    markdownSyntaxConfig: chatTaskFeatureConfig?.markdownSyntaxConfig,
    enableEnrichedQMSFeedback: chatTaskFeatureConfig?.enableEnrichedQMSFeedback,
  };
}

function getParamsConfigsForContext(
  reviewLevel: ReviewLevel | undefined,
  params: MultiTurnInteractionConfiguration,
) {
  return {
    restrictRollbacks:
      reviewLevel === ReviewLevel.ReviewConsensus ||
      (!_.isNil(reviewLevel) &&
        params.restrictRollbacksInReview &&
        reviewLevel !== ReviewLevel.Attempt),
    forceHistoryReset: params.forceHistoryReset,
    forceHistoryResetForResponseChanges: params.forceHistoryResetForResponseChanges,
    resetChatHistoryOnEachTurn: params.resetChatHistoryOnEachTurn,
    passDownOriginalAttachments: params.passDownOriginalAttachments,
  };
}

export function useCommonChatTaskContextConfigs(
  params: MultiTurnInteractionConfiguration,
  reviewLevel: ReviewLevel | undefined,
  chatTaskFeatureConfig: IChatTaskFeatureConfig | undefined,
  taskTemplateVariables?: Record<string, string>,
) {
  return useMemo(
    () => ({
      ...getDisableCopyingAndPastingConfigsForContext(
        reviewLevel,
        chatTaskFeatureConfig,
        taskTemplateVariables,
      ),
      ...getChatTaskFeatureConfigsForContext(chatTaskFeatureConfig),
      ...getParamsConfigsForContext(reviewLevel, params),
    }),
    [params, reviewLevel, chatTaskFeatureConfig, taskTemplateVariables],
  );
}
